/* import __COLOCATED_TEMPLATE__ from './mcp-actions-list-component.hbs'; */
/* RESPONSIBLE TEAM: team-ml */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { DEFAULT_ACTION_COLUMNS } from 'embercom/models/workflow-connector/constants/available-action-columns';
import type WorkflowConnectorInsertableActionsService from 'embercom/services/workflow-connector-insertable-actions';
import { action } from '@ember/object';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';

interface Signature {
  Args: Args;
}

interface Args {
  workflowActions: Array<any>;
  selectedColumns?: Array<any>;
  title?: string | null;
}

export default class McpActionsListComponent extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare intercomEventService: any;
  @service declare intercomConfirmService: any;
  @service declare workflowConnectorInsertableActions: WorkflowConnectorInsertableActionsService;
  @service declare notificationsService: any;
  @service declare store: any;
  @service declare router: any;

  get selectedColumns() {
    let selectedColumns;

    if (this.args.selectedColumns) {
      selectedColumns = this.args.selectedColumns;
    } else {
      selectedColumns = DEFAULT_ACTION_COLUMNS;
    }

    return selectedColumns?.map((column: any) => {
      if (column.valuePath === 'name') {
        column.label = this.intl.t('workflow-connector.list.columns.name');
      } else {
        column.label = this.intl.t(`workflow-connector.list.columns.${column.valuePath}`);
      }

      if (column.hasTooltip) {
        if (column.valuePath === 'customerVerification') {
          column.tooltip = this.intl.t(
            `workflow-connector.list.columns.${column.valuePath}-tooltip`,
          );
        } else {
          column.tooltip = this.intl.t(
            `workflow-connector.list.columns.${column.valuePath}-tooltip`,
          );
        }
      }

      return column;
    });
  }

  get customActionsRoute() {
    if (this.appService.app?.canUseFeature('data-connectors-health-page')) {
      return 'apps.app.settings.app-settings.custom-actions.custom-action-health';
    }
    return 'apps.app.settings.app-settings.custom-actions.custom-action';
  }

  @action
  deleteAction(workflowAction: any) {
    taskFor(this.deleteActionTask).perform(workflowAction);
  }

  @dropTask
  *deleteActionTask(workflowAction: any): TaskGenerator<void> {
    let confirmOptions = {
      title: this.intl.t('workflow-connector.builder.delete-modal.title'),
      confirmButtonText: this.intl.t('workflow-connector.builder.delete-modal.delete-button'),
      cancleButtonText: this.intl.t('workflow-connector.builder.delete-modal.cancel-button'),
      body: this.intl.t('workflow-connector.builder.delete-modal.body'),
      primaryButtonType: 'primary-destructive',
    };

    let isConfirmed = yield this.intercomConfirmService.confirm(confirmOptions);
    if (!isConfirmed) {
      return;
    }

    try {
      let actionId = workflowAction.id;

      let action = this.store.peekRecord('workflow-connector/action', actionId);
      if (!action) {
        action = yield this.store.findRecord('workflow-connector/action', actionId);
      }

      yield action.destroyRecord();

      this.store.unloadRecord(this.store.peekRecord('workflow-connector/action', actionId));
      this.store.unloadRecord(this.store.peekRecord('workflow-connector/setup-action', actionId));

      // Refetch insertable-actions since available actions have changed
      this.workflowConnectorInsertableActions.refetch();

      this.notificationsService.notifyConfirmation(
        this.intl.t('workflow-connector.builder.delete-modal.success'),
      );
    } catch (e) {
      this.notificationsService.notifyError(
        this.intl.t('workflow-connector.builder.delete-modal.error'),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'WorkflowConnector::McpActionsListComponent': typeof McpActionsListComponent;
    'workflow-connector/mcp-actions-list-component': typeof McpActionsListComponent;
  }
}
